import {
  faCircleCheck,
  faCircleMinus,
  faCirclePlus,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  InfoTip,
  LinkButtonV2,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';

export interface ExpandablePlanWrapperProps {
  displayName: string;
  shortDescription: string;
  totalSolutionCount: number;
  familyIndex?: number;
  solutionIndex?: number;
  clearSelection?: (amilyIndex?: number, solutionIndex?: number) => void;
  isSelected?: boolean;
  selectedSolutionLabel?: string;
  isAddon?: boolean;
}

export const ExpandablePlanWrapper: FunctionComponent<
  PropsWithChildren<ExpandablePlanWrapperProps>
> = ({
  displayName,
  shortDescription,
  totalSolutionCount,
  isAddon,
  clearSelection,
  selectedSolutionLabel,
  solutionIndex,
  familyIndex,
  isSelected,
  children,
}) => {
  const [isWrapperOpen, setIsWrapperOpen] = useState<boolean>(false);
  const handleOnClick = useCallback(() => {
    setIsWrapperOpen(!isWrapperOpen);
  }, [isWrapperOpen]);

  const handleClearSelection = useCallback(() => {
    if (
      clearSelection &&
      familyIndex !== undefined &&
      solutionIndex !== undefined
    ) {
      clearSelection(familyIndex, solutionIndex);
    }
  }, [clearSelection, familyIndex, solutionIndex]);

  const icon = useMemo(() => {
    if (isWrapperOpen) {
      return faCircleMinus;
    }
    return faCirclePlus;
  }, [isWrapperOpen]);

  const displayLabel = useMemo(() => {
    if (isAddon) {
      return 'Add-ons';
    } else if (totalSolutionCount > 1) {
      return displayName;
    }
    return 'Plan';
  }, [displayName, isAddon, totalSolutionCount]);

  return (
    <div className="flex flex-col rounded-xl shadow-md py-8 pr-8 border border-danger-100">
      <button
        data-testid="open-wrapper-btn"
        onClick={handleOnClick}
        type="button"
      >
        <div
          className={`${
            isWrapperOpen ? 'pb-8' : ''
          } grid grid-cols-12 w-full justify-between items-center`}
        >
          {isSelected && (
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="col-span-1 items-center pl-6 text-success-800 h-7 w-7"
            />
          )}
          <div className="col-start-2 col-span-9 pl-4 2xl:pl-0 text-lg font-semibold text-left flex gap-x-4">
            <span className="text-secondary-800">{displayLabel}</span>
            {shortDescription ? (
              <InfoTip
                id={displayName}
                tip={{ title: '', content: textWrap(shortDescription) }}
                colorClass="text-primary-800"
              />
            ) : null}
          </div>
          <div className="col-span-2 flex flex-row items-center justify-end gap-x-8">
            {!isAddon &&
              selectedSolutionLabel &&
              clearSelection &&
              isSelected && (
                <div className="flex flex-row items-center gap-x-5 h-max">
                  <span className="font-bold text-black">
                    {selectedSolutionLabel}
                  </span>
                  <LinkButtonV2
                    linkText="Clear"
                    onClick={handleClearSelection}
                    className="underline"
                    test_id="clear-selection-btn"
                  />
                </div>
              )}
            <FontAwesomeIcon icon={icon} className="text-secondary-800 fa-2x" />
          </div>
        </div>
      </button>
      {isWrapperOpen && children}
    </div>
  );
};

const textWrap = (text: string, maxLine = 100) => {
  let pointer = 0;
  let spacePointer = 0;
  let lastLineBreak = 0;
  let lines = [];

  while (pointer <= text.length - 1) {
    if (text[pointer] === ' ') {
      spacePointer = pointer;
    }

    if (pointer - lastLineBreak === maxLine) {
      lines.push(text.slice(lastLineBreak, spacePointer));
      lastLineBreak = spacePointer;
    }

    pointer += 1;
  }

  if (pointer - lastLineBreak > 0) {
    lines.push(text.slice(lastLineBreak, pointer));
  }

  return (
    <>
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    </>
  );
};
